import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messageOf, unwrap } from '@truecare/api-client';
import type {
  AvailabilityCreateRequest,
  AvailabilityResponse,
} from '@truecare/schemas';
import { Alert, Button, Card, CardHeader } from '@truecare/ui';
import { api } from '../api';
import { AvailabilityCreateModal } from './AvailabilityCreateModal';

/**
 * Phase 5f-d — caregiver availability CRUD UI.
 *
 * Fetches GET /me/caregiver/availability + renders the list (WEEKLY
 * blocks first, then ONE_OFF; both sub-sorted). "Add availability"
 * opens <AvailabilityCreateModal>; per-row delete fires
 * DELETE /me/caregiver/availability/{id}.
 *
 * Backend endpoints already shipped in Phase 9d (3 endpoints: list,
 * create, delete). The matching engine (Phase 9b) reads availability
 * to filter eligible caregivers — without availability blocks set,
 * the caregiver won't receive shift offers for any time slots.
 *
 * Per 5f-1 lock: section mounts for APPLYING + ACTIVE statuses; the
 * parent /me page gates visibility.
 */

const DAY_LABELS: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

// Audit L2 (2026-09-01) — the availability list showed the raw IANA id
// ("America/Toronto"), which reads as developer-facing to a phone-first, varied-
// age audience. Show the friendly label used elsewhere (the create modal already
// says "Eastern"). v1 is Ontario-only, so a small Eastern map covers it.
function friendlyTz(iana: string | null | undefined): string {
  if (!iana) return '';
  const map: Record<string, string> = {
    'America/Toronto': 'Eastern',
    'America/Nipigon': 'Eastern',
    'America/Thunder_Bay': 'Eastern',
    'America/Atikokan': 'Eastern (no DST)',
  };
  return map[iana] ?? iana;
}

function describeBlock(block: AvailabilityResponse): string {
  if (block.recurrenceType === 'WEEKLY') {
    const day = block.weeklyDayOfWeek
      ? (DAY_LABELS[block.weeklyDayOfWeek] ?? '?')
      : '?';
    const start = block.weeklyStartLocalTime ?? '?';
    const end = block.weeklyEndLocalTime ?? '?';
    const tz = friendlyTz(block.weeklyTimezone) || '?';
    // Audit L1 (2026-09-01) — friendly-format the effective-range dates instead
    // of the raw ISO "2026-07-30", to match the one-off block's friendly style.
    const friendlyDate = (iso: string): string =>
      new Date(`${iso}T00:00:00`).toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    const range =
      block.weeklyEffectiveFrom || block.weeklyEffectiveTo
        ? ` (${block.weeklyEffectiveFrom ? friendlyDate(block.weeklyEffectiveFrom) : 'now'} – ${
            block.weeklyEffectiveTo ? friendlyDate(block.weeklyEffectiveTo) : 'ongoing'
          })`
        : '';
    return `${day}s, ${start}–${end} ${tz}${range}`;
  }
  // ONE_OFF — render in caregiver's browser local TZ
  const start = block.startUtc ? new Date(block.startUtc) : null;
  const end = block.endUtc ? new Date(block.endUtc) : null;
  if (!start || !end) return 'One-time block (date unset)';
  const fmt = (d: Date) =>
    d.toLocaleString('en-CA', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  return `${fmt(start)} – ${fmt(end)}`;
}

function sortBlocks(blocks: AvailabilityResponse[]): AvailabilityResponse[] {
  return [...blocks].sort((a, b) => {
    // Weekly first
    if (a.recurrenceType !== b.recurrenceType) {
      return a.recurrenceType === 'WEEKLY' ? -1 : 1;
    }
    if (a.recurrenceType === 'WEEKLY' && b.recurrenceType === 'WEEKLY') {
      const dayDiff = (a.weeklyDayOfWeek ?? 0) - (b.weeklyDayOfWeek ?? 0);
      if (dayDiff !== 0) return dayDiff;
      return (a.weeklyStartLocalTime ?? '').localeCompare(b.weeklyStartLocalTime ?? '');
    }
    // Both ONE_OFF — by startUtc ascending
    return (a.startUtc ?? '').localeCompare(b.startUtc ?? '');
  });
}

export function CaregiverAvailabilitySection() {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Phase 5f-d audit r1 — track per-row delete state so only the
  // affected row's Remove button shows the spinner / disabled state,
  // and surface delete errors to the caregiver instead of swallowing.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['caregiver', 'availability'],
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/availability')),
  });

  const createMutation = useMutation({
    mutationFn: (body: AvailabilityCreateRequest) =>
      unwrap(api.POST('/api/v1/me/caregiver/availability', { body })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caregiver', 'availability'] });
      setShowModal(false);
      setSubmitError(null);
    },
    onError: (e) => {
      setSubmitError(messageOf(e, 'Could not save availability.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE('/api/v1/me/caregiver/availability/{id}', {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['caregiver', 'availability'] });
      setDeletingId(null);
      setDeleteError(null);
    },
    onError: (e) => {
      setDeletingId(null);
      setDeleteError(messageOf(e, 'Could not remove availability.'));
    },
  });

  if (listQuery.isPending) {
    return (
      <Card padding="lg">
        <p className="font-body text-sm text-ink-muted">Loading your availability…</p>
      </Card>
    );
  }

  if (listQuery.isError || !listQuery.data) {
    return (
      <Card padding="lg">
        <Alert tone="danger" title="Couldn't load your availability">
          Please refresh the page.
        </Alert>
      </Card>
    );
  }

  // Backend GET /me/caregiver/availability returns
  // `AvailabilityListResponse = { items: AvailabilityResponse[] }`.
  const items = listQuery.data.items;
  const blocks = sortBlocks(items);

  return (
    <>
      <Card padding="lg" data-testid="caregiver-availability-section">
        <CardHeader
          title="Availability"
          description="Set the days + times you're available to take shifts. Recurring weekly blocks cover most of your schedule; add one-time blocks for specific dates."
        />
        <div className="mt-4">
          {blocks.length === 0 ? (
            <p
              className="font-body text-sm text-ink-muted"
              data-testid="caregiver-availability-empty"
            >
              You haven't set any availability yet. Add at least one block so
              we can match you to shifts.
            </p>
          ) : (
            <>
              {deleteError && (
                <Alert
                  tone="danger"
                  className="mb-3"
                  title="Couldn't remove availability"
                  data-testid="caregiver-availability-delete-error"
                >
                  {deleteError}
                </Alert>
              )}
              <ul className="flex flex-col gap-2">
                {blocks.map((b) => (
                  <li
                    key={b.id}
                    className="flex items-center justify-between gap-3 border-b border-border py-2 last:border-b-0"
                    data-testid={`availability-row-${b.id}`}
                  >
                    <div className="flex flex-col">
                      <span className="font-body text-sm text-forest">
                        {describeBlock(b)}
                      </span>
                      <span className="text-xs text-ink-muted">
                        {b.recurrenceType === 'WEEKLY' ? 'Weekly recurring' : 'One-time'}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDeleteError(null);
                        setDeletingId(b.id);
                        deleteMutation.mutate(b.id);
                      }}
                      loading={deletingId === b.id}
                      disabled={deletingId !== null && deletingId !== b.id}
                      data-testid={`availability-row-${b.id}-delete`}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => {
              setSubmitError(null);
              setShowModal(true);
            }}
            data-testid="caregiver-availability-add"
          >
            + Add availability
          </Button>
        </div>
      </Card>

      <AvailabilityCreateModal
        open={showModal}
        error={submitError}
        onCancel={() => {
          setShowModal(false);
          setSubmitError(null);
        }}
        onSubmit={async (body) => {
          await createMutation.mutateAsync(body);
        }}
      />
    </>
  );
}
